import { kartoyImages } from '@mobile/assets/kartoy'
import {
  type BackendTransaction,
  type CardTemplateId,
  fetchCards,
  fetchTransactions,
  selectedCardIssue,
  selectedCards,
  selectedCardTemplatesValidTimeMs,
  selectedTransactions,
} from '@shared-core/features/backend'
import {
  filterOperationsByCard,
  formatOperationMoney,
  type Operation,
  OperationStatus,
  parseBackendOperation,
} from '@shared-core/features/operations'
import { useAuthorizationGate } from '@shared-core/hooks/use-authorization-gate'
import { formatDate, useDate } from '@shared-core/hooks/use-date'
import { useI18N } from '@shared-core/hooks/use-i18n'
import { useSharedPoller } from '@shared-core/hooks/use-shared-poller'
import { ScreensList, useNavigation } from '@shared-core/navigation'
import { useAppDispatch, useAppSelector } from '@shared-core/store'
import { formatWithThousandSeparators } from '@shared-core/utils/balances/balances'
import { AppTransactionRow } from '@shared-ui/components/app/transaction-row'
import { AppTransactionRowState } from '@shared-ui/components/app/transaction-row/types'
import { AppIcon } from '@shared-ui/components/ui/app-icon'
import { FastImage } from '@shared-ui/components/ui/fast-image'
import { Column, Row } from '@shared-ui/components/ui/grid'
import { GText } from '@shared-ui/components/ui/text'
import { useAppState } from '@shared-ui/hooks/rn/use-app-state'
import { memo, useMemo } from 'react'
import { View } from 'react-native'
import Animated, { FadeInDown } from 'react-native-reanimated'
import locales from './index.i18n.json'
import { useCreateStyles } from './styles'

function getOperationTimeLabel(timestamp: number): string {
  if (!timestamp) return ''
  const date = new Date(timestamp)

  if (Number.isNaN(date.getTime())) return ''
  return formatDate(date, 'HH:mm')
}

// Преобразуем операции API для списка главного экрана.
function getOperations(data: BackendTransaction[] | null): Operation[] {
  if (!data) return []
  return data.flatMap((item) => {
    const operation = parseBackendOperation(item)
    return operation ? [operation] : []
  })
}

// Декоративные скелетоны пустого состояния «Операции»: стопка карточек, уходящих вправо-вверх.
function OperationsStub() {
  const styles = useCreateStyles()

  return (
    <View pointerEvents="none" style={styles.stubWrapper}>
      <View style={styles.stubCardBack}>
        <Row align="center" flex={1} gap={9}>
          <View style={styles.stubCircleBack} />
          <Column gap={5}>
            <View style={styles.stubLineBackTitle} />
            <View style={styles.stubLineBackSub} />
          </Column>
          <Column align="flex-end" flex={1} gap={5}>
            <View style={styles.stubLineBackTitle} />
            <View style={styles.stubLineBackSub} />
          </Column>
        </Row>
      </View>
      <View style={styles.stubCardFront}>
        <Row align="center" flex={1} gap={10}>
          <View style={styles.stubCircleFront} />
          <Column gap={6}>
            <View style={styles.stubLineFrontTitle} />
            <View style={styles.stubLineFrontSub} />
          </Column>
          <Column align="flex-end" flex={1} gap={6}>
            <View style={styles.stubLineFrontTitle} />
            <View style={styles.stubLineFrontSub} />
          </Column>
        </Row>
      </View>
    </View>
  )
}

type OperationItemProps = { operation: Operation }

const OperationItem = memo(function OperationItemComponent({ operation }: OperationItemProps) {
  const styles = useCreateStyles()
  const navigation = useNavigation()
  const { t } = useI18N(locales)
  const titleKeys = {
    'card-issue': 'card-issue',
    withdraw: 'tx-withdraw',
    topup: 'tx-topup',
    purchase: 'purchase',
    'cancelled-purchase': 'purchase',
  } as const
  const title =
    operation.kind === 'withdraw' ? t('tx-withdraw') : operation.title.trim() || t(titleKeys[operation.kind])
  // Для пополнения, вывода, выпуска карты и успешной покупки второй строкой — точное время операции.
  const subtitle =
    operation.kind === 'topup' ||
    operation.kind === 'withdraw' ||
    operation.kind === 'card-issue' ||
    (operation.kind === 'purchase' && operation.status === OperationStatus.Completed)
      ? getOperationTimeLabel(operation.createdAt)
      : (operation.subtitle ?? '')

  const isCompletedTopup = operation.kind === 'topup' && operation.status === OperationStatus.Completed

  let state = AppTransactionRowState.Success

  if (operation.status === OperationStatus.Pending) state = AppTransactionRowState.Pending

  if (operation.status === OperationStatus.Failed) state = AppTransactionRowState.Cancelled
  let avatarContent = (
    <GText variant="body-16-medium" isSecondary>
      {title[0]}
    </GText>
  )

  if (operation.kind === 'card-issue') avatarContent = <AppIcon icon="card" size={24} isSecondary />

  if (operation.kind === 'topup') avatarContent = <AppIcon icon="topup" size={24} isSecondary />
  let avatar = <View style={styles.operationIcon}>{avatarContent}</View>

  if (operation.kind === 'withdraw') {
    avatar = <FastImage source={kartoyImages.tBank} style={styles.operationIcon} resizeMode="cover" />
  }

  let secondaryAmount = operation.debitAmount

  if (operation.debitAmount && operation.billingCurrency) {
    secondaryAmount = formatOperationMoney(operation.debitAmount, operation.billingCurrency)
  }

  if (isCompletedTopup) secondaryAmount = undefined

  return (
    <AppTransactionRow
      title={title}
      subtitle={subtitle}
      amount={operation.fiatAmount}
      secondaryAmount={secondaryAmount}
      state={state}
      isIncome={isCompletedTopup}
      avatar={avatar}
      onPress={() => navigation.navigate(ScreensList.TX_DETAILS, { operation })}
    />
  )
})

type HomeOperationsProps = { isPanel?: boolean; cardId?: string | null; templateId?: CardTemplateId }

export function HomeOperations({ isPanel = false, cardId, templateId }: HomeOperationsProps) {
  const styles = useCreateStyles()
  const { t } = useI18N(locales)
  const humanizeDate = useDate()
  const dispatch = useAppDispatch()
  const { isAuthorized } = useAuthorizationGate()
  const [appState] = useAppState()
  const cardTemplatesValidTimeMs = useAppSelector(selectedCardTemplatesValidTimeMs)
  // История и карты обновляются одним циклом на все экраны, где она показана.
  useSharedPoller({
    key: 'card-history',
    enabled: isAuthorized && appState === 'active',
    intervalMs: cardTemplatesValidTimeMs,
    run: async () => {
      await Promise.all([dispatch(fetchTransactions()), dispatch(fetchCards())])
      return undefined
    },
  })
  const transactions = useAppSelector(selectedTransactions)
  const cardIssue = useAppSelector(selectedCardIssue)
  const cards = useAppSelector(selectedCards)
  // Добавляем счёт выпуска сразу, пока история сервера ещё обновляется.
  const operations = useMemo(() => {
    const items = getOperations(transactions)
    const invoice = cardIssue.invoice
    const cardIds = (cards ?? []).flatMap((card) =>
      card.type === templateId || card.templateId === templateId ? [card.id] : [],
    )

    if (cardId) cardIds.push(cardId)

    if (invoice && invoice.templateId === templateId) cardIds.push(invoice.cardId)
    const cardHistory = templateId ? { templateId, cardIds } : undefined

    const invoiceTxId = invoice?.txId

    if (!invoice || items.some((item) => item.txId === invoiceTxId || item.id === invoiceTxId)) {
      return filterOperationsByCard(items, cardId, cardHistory)
    }
    let status = OperationStatus.Pending

    if (invoice.state === 'completed') status = OperationStatus.Completed

    if (invoice.state === 'failed' || invoice.state === 'expired') status = OperationStatus.Failed
    return filterOperationsByCard(
      [
        {
          id: invoice.txId,
          txId: invoice.txId,
          cardId: invoice.cardId,
          templateId: invoice.templateId,
          kind: 'card-issue' as const,
          title: t('card-issue'),
          subtitle: '',
          createdAt: invoice.createdAt ?? 0,
          fiatAmount: `₽${formatWithThousandSeparators(invoice.amount.replace(/\.00$/, ''))}`,
          status,
          direction: 'out' as const,
        },
        ...items,
      ],
      cardId,
      cardHistory,
    )
  }, [transactions, cardIssue.invoice, cards, cardId, templateId, t])
  const hasOperations = operations.length > 0
  const operationGroups = useMemo(
    () =>
      operations.reduce<Record<string, Operation[]>>((groups, operation) => {
        const date = new Date(operation.createdAt)
        const dateLabel = Number.isNaN(date.getTime()) ? '' : humanizeDate(formatDate(date, 'dd.MM.yyyy'))
        groups[dateLabel] = [...(groups[dateLabel] ?? []), operation]
        return groups
      }, {}),
    [operations, humanizeDate],
  )
  return (
    <Animated.View
      entering={FadeInDown.delay(260).duration(420)}
      style={[styles.operations, isPanel && styles.operationsPanel]}
    >
      {hasOperations && !isPanel && (
        <GText fw={600} style={styles.sectionTitle}>
          {t('operations')}
        </GText>
      )}
      {hasOperations && (
        <Column gap={16} style={styles.operationsList}>
          {Object.entries(operationGroups).map(([dateLabel, items]) => (
            <Column key={dateLabel} gap={4} style={styles.operationGroup}>
              <GText fw={500} style={styles.operationGroupTitle}>
                {dateLabel}
              </GText>
              {items.map((operation) => (
                <OperationItem key={operation.id} operation={operation} />
              ))}
            </Column>
          ))}
        </Column>
      )}
      {!hasOperations && (
        <Column align="center" gap={16} style={styles.operationsEmpty}>
          <OperationsStub />
          <GText style={styles.operationsEmptyText}>{t('operations-empty')}</GText>
        </Column>
      )}
    </Animated.View>
  )
}
